/**
 * 权限检查工具类
 * 根据用户角色检查是否有特定操作权限
 */

/**
 * 获取用户的角色代码列表
 * @param {Object} user - 当前用户对象
 * @returns {Array<string>} 角色代码列表
 */
export const getUserRoleCodes = (user) => {
  if (!user) return [];
  
  // 如果是超级管理员或 is_staff，返回包含 super_admin 的列表
  if (user.is_superuser) return ['super_admin'];
  if (user.is_staff) return ['admin'];
  
  // 从 roles 或 roles_info 获取角色代码
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.map(r => (typeof r === 'string' ? r : r.code || r.name));
  }
  
  if (user.roles_info && Array.isArray(user.roles_info)) {
    return user.roles_info.map(r => r.code || r.name);
  }
  
  return [];
};

/**
 * 检查用户是否有指定角色
 * @param {Object} user - 当前用户对象
 * @param {string|Array<string>} roles - 角色代码或代码数组
 * @returns {boolean} 是否有指定角色
 */
export const hasRole = (user, roles) => {
  const userRoles = getUserRoleCodes(user);
  const roleList = Array.isArray(roles) ? roles : [roles];
  return roleList.some(role => userRoles.includes(role));
};

/**
 * 检查用户是否是管理员（admin 或 super_admin）
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否是管理员
 */
export const isAdmin = (user) => {
  return hasRole(user, ['admin', 'super_admin']) || user?.is_staff || user?.is_superuser;
};

/**
 * 检查用户是否是超级管理员
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否是超级管理员
 */
export const isSuperAdmin = (user) => {
  return hasRole(user, 'super_admin') || user?.is_superuser;
};

/**
 * 检查用户是否是部门经理
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否是部门经理
 */
export const isDepartmentManager = (user) => {
  return hasRole(user, 'department_manager');
};

/**
 * 检查用户是否可以创建用户
 * 只有 admin 和 super_admin 可以创建用户
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以创建用户
 */
export const canCreateUser = (user) => {
  return isAdmin(user);
};

/**
 * 检查用户是否可以编辑用户
 * super_admin 可以编辑所有用户
 * admin 可以编辑所有用户
 * department_manager 只能编辑自己部门的用户（在 API 层验证）
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以编辑用户
 */
export const canEditUser = (user) => {
  return isAdmin(user) || isDepartmentManager(user);
};

/**
 * 检查用户是否可以删除用户
 * 只有 admin 和 super_admin 可以删除用户
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以删除用户
 */
export const canDeleteUser = (user) => {
  return isAdmin(user);
};

/**
 * 检查用户是否可以分配角色
 * super_admin: 无限制
 * admin: 可以在 employee 和 department_manager 之间切换
 * 其他: 不可分配
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以分配角色
 */
export const canAssignRoles = (user) => {
  return isAdmin(user);
};

/**
 * 检查用户是否可以锁定/解锁用户
 * admin 和 super_admin 可以锁定用户
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以锁定/解锁用户
 */
export const canLockUser = (user) => {
  return isAdmin(user);
};

/**
 * 检查用户是否可以重置用户密码
 * admin 和 super_admin 可以重置密码
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以重置密码
 */
export const canResetPassword = (user) => {
  return isAdmin(user);
};

/**
 * 检查用户是否可以编辑用户的部门（如果是 department_manager，则不能编辑）
 * 只有 admin 和 super_admin 可以编辑部门
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以编辑部门
 */
export const canEditDepartment = (user) => {
  return isAdmin(user);
};

/**
 * 检查用户是否可以创建部门
 * 只有 admin 和 super_admin 可以创建部门
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以创建部门
 */
export const canCreateDepartment = (user) => {
  return isAdmin(user);
};

/**
 * 检查用户是否可以创建角色
 * 只有 super_admin 可以创建角色
 * @param {Object} user - 当前用户对象
 * @returns {boolean} 是否可以创建角色
 */
export const canCreateRole = (user) => {
  return isSuperAdmin(user);
};

export default {
  getUserRoleCodes,
  hasRole,
  isAdmin,
  isSuperAdmin,
  isDepartmentManager,
  canCreateUser,
  canEditUser,
  canDeleteUser,
  canAssignRoles,
  canLockUser,
  canResetPassword,
  canEditDepartment,
  canCreateDepartment,
  canCreateRole,
};
